import { Clipboard } from '@angular/cdk/clipboard';
import { HorizontalConnectionPos, OriginConnectionPosition, Overlay, OverlayConnectionPosition, 
		 OverlayPositionBuilder, OverlayRef, VerticalConnectionPos } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Directive, ElementRef, HostListener, Input } from '@angular/core';
import { TooltipComponent, TooltipPosition } from '@angular/material/tooltip';

@Directive({
  	selector: '[copyToClipboard]',
	exportAs: 'copyToClipboard'
})
export class CopyToClipboardDirective {

	private copiedTimeout;

	copied: boolean = false;

	@Input() copyToClipboard: string;
	@Input('copyToClipboardTooltip') tooltipMessage: string;
	@Input('copyToClipboardTooltipPosition') tooltipPosition: TooltipPosition = 'above';

	@HostListener('click', ['$event']) onClick (event: MouseEvent) {

		this.copied = this.clipboard.copy(this.copyToClipboard);

		if(this.copied) {

			let tooltipRef: ComponentRef<TooltipComponent> = null;

			if(!!this.tooltipMessage) {

				const scrollStrategy = this.overlay.scrollStrategies.reposition();
				const positionStrategy = this.overlayPositionBuilder
											.flexibleConnectedTo(this.elementRef)
											.withPositions([
												{...this.origin.main, ...this.overlayPosition.main},
												{...this.origin.fallback, ...this.overlayPosition.fallback}
											]);
	
				const tooltipPortal = new ComponentPortal(TooltipComponent);
				const overlayRef: OverlayRef = this.overlay.create({positionStrategy, scrollStrategy});

				tooltipRef = overlayRef.attach(tooltipPortal);	
				tooltipRef.instance.message = this.tooltipMessage;
				tooltipRef.instance.show(0);
				tooltipRef.instance.afterHidden().subscribe(() => {
					overlayRef.detach();
					tooltipRef.destroy();
				});
			}

			if(this.copiedTimeout)
				clearTimeout(this.copiedTimeout);
		
			this.copiedTimeout = setTimeout(() => {
				this.copied = false;
	
				if(tooltipRef)
					tooltipRef.instance.hide(0);

			}, 3000);
		}

		event.stopPropagation();
	}

	constructor(private clipboard: Clipboard,
				private elementRef: ElementRef,
				private overlay: Overlay,
				private overlayPositionBuilder: OverlayPositionBuilder) {}

	private invertPosition(x: HorizontalConnectionPos, y: VerticalConnectionPos) {
		if (this.tooltipPosition === 'above' || this.tooltipPosition === 'below') {
			if (y === 'top') y = 'bottom';
			else if (y === 'bottom') y = 'top';
		} else {
			if (x === 'end') x = 'start';
			else if (x === 'start') x = 'end';
		}
		return {x, y};
	}

	private get origin (): {main: OriginConnectionPosition; fallback: OriginConnectionPosition} {
		const position = this.tooltipPosition;
		let originPosition: OriginConnectionPosition;
	
		if (position == 'above' || position == 'below')
			originPosition = {originX: 'center', originY: position == 'above' ? 'top' : 'bottom'};
		else if (position == 'before' || position == 'left')
			originPosition = {originX: 'start', originY: 'center'};
		else if (position == 'after' || position == 'right')
			originPosition = {originX: 'end', originY: 'center'};
	
		const {x, y} = this.invertPosition(originPosition!.originX, originPosition!.originY);
	
		return {
			main: originPosition!,
			fallback: {originX: x, originY: y},
		};
	}

	private get overlayPosition (): {main: OverlayConnectionPosition; fallback: OverlayConnectionPosition} {
		const position = this.tooltipPosition;
		let overlayPosition: OverlayConnectionPosition;
	
		if (position == 'above')
			overlayPosition = {overlayX: 'center', overlayY: 'bottom'};
		else if (position == 'below')
			overlayPosition = {overlayX: 'center', overlayY: 'top'};
		else if (position == 'before' || position == 'left')
			overlayPosition = {overlayX: 'end', overlayY: 'center'};
		else if (position == 'after' || position == 'right')
		  	overlayPosition = {overlayX: 'start', overlayY: 'center'};
	
		const {x, y} = this.invertPosition(overlayPosition!.overlayX, overlayPosition!.overlayY);
	
		return {
			main: overlayPosition!,
			fallback: {overlayX: x, overlayY: y},
		};
	}
}
